import React, { useState } from 'react';
import classNames from 'classnames';
import IconButton from '../IconButton';
import ToolbarDropdown from './ToolbarDropdown';

/**
 * Root class strings per variant. Each value is a WHOLE literal string (no
 * runtime concatenation) so Tailwind's content scanner sees every utility and
 * the literal `toolbar-${variant}` hook stays in the DOM — `toolbar` and
 * `toolbar-floating` are reached from other still-CSS stylesheets (diagram
 * flows, Project, OET) and must remain literal.
 *
 * Variant-scoped descendant rules from the old SCSS are expressed as arbitrary
 * descendant variants on the root, targeting the literal child hooks
 * (`.toolbar-button`, `.text-button`, `.children`, `.icon-button-floating`).
 * The `-dropdown` suffix variants only flip the flex direction to column.
 */
const VARIANT_CLASSES: Record<string, string> = {
  panel:
    'toolbar-panel ' +
    '[&_.toolbar-button]:shadow-[inset_1px_0_0_0_var(--color-grey)] ' +
    '[&_.toolbar-dropdown_.children]:bg-light ' +
    '[&_.toolbar-dropdown_.children]:shadow-[inset_1px_-1px_0_0_var(--color-grey),0_6px_12px_-4px_rgba(0,0,0,0.2)] ' +
    '[&_.toolbar-dropdown_.children]:top-[37px] [&_.toolbar-dropdown_.children]:right-[15px] [&_.toolbar-dropdown_.children]:z-10 ' +
    '[&_.toolbar-dropdown_.children_.toolbar-button:hover]:bg-[#f5f7fa] [&_.toolbar-dropdown_.children_.toolbar-button:hover]:text-primary',
  floating:
    'toolbar-floating absolute bottom-[25px] right-[25px] bg-light border border-grey3 rounded ' +
    '[&_.toolbar-button]:border-r [&_.toolbar-button]:border-grey3 [&_.toolbar-button:last-child]:border-r-0 ' +
    '[&_.icon-button-floating]:p-[5px] [&_.icon-button-floating]:h-auto [&_.icon-button-floating]:w-auto ' +
    '[&_.icon-button-floating_svg]:h-[14px] [&_.icon-button-floating_svg]:w-auto ' +
    '[&_.text-button]:text-grey2 [&_.text-button]:py-0 [&_.text-button]:h-6 [&_.text-button]:leading-6 [&_.text-button]:align-middle ' +
    '[&_.text-button:hover]:text-primary',
  appbar:
    'toolbar-appbar relative pl-[15px] [&>div]:mr-[15px] ' +
    "after:content-[''] after:absolute after:right-0 after:top-[5px] after:bottom-[5px] after:border-r after:border-grey " +
    '[&_.toolbar-button]:w-full [&_.toolbar-button_.text-button]:w-full [&_.toolbar-button_.text-button]:text-center ' +
    '[&_.toolbar-dropdown_.children]:bg-white [&_.toolbar-dropdown_.children]:top-[50px] [&_.toolbar-dropdown_.children]:z-20 ' +
    '[&_.toolbar-dropdown_.children]:shadow-[2px_2px_4px_0_rgba(0,0,0,0.04),-2px_-2px_4px_0_rgba(0,0,0,0.04),0_6px_12px_-4px_rgba(0,0,0,0.2)] ' +
    '[&_.toolbar-dropdown_.children_.toolbar-dropdown_.children]:top-0 [&_.toolbar-dropdown_.children_.toolbar-dropdown_.children]:left-0 [&_.toolbar-dropdown_.children_.toolbar-dropdown_.children]:right-auto ' +
    '[&_.toolbar-dropdown_.children_.toolbar-button]:text-black ' +
    '[&_.toolbar-dropdown_.children_.toolbar-button:hover]:bg-[#f5f7fa] [&_.toolbar-dropdown_.children_.toolbar-button:hover]:text-primary',
  'panel-dropdown': 'toolbar-panel-dropdown flex-col',
  'appbar-dropdown': 'toolbar-appbar-dropdown flex-col',
  'appbar-dropdown-dropdown': 'toolbar-appbar-dropdown-dropdown flex-col',
  'floating-dropdown': 'toolbar-floating-dropdown',
};

/**
 * The handler for a toolbar button: either the literal string `'dropdown'`
 * (which makes the Toolbar own the dropdown open/closed state) or a click
 * callback.
 */
export type ToolbarButtonHandler = 'dropdown' | (() => void);

/** The dropdown config carried by a button. */
export interface ToolbarDropdownConfig {
  /** The buttons rendered inside the dropdown. */
  buttons: ToolbarButton[];
  /** Should an arrow be rendered to open the dropdown? */
  arrow?: boolean;
}

/**
 * A single toolbar button.
 *
 * `icon` / `dropdown` are `unknown` because the toolbar's call sites (the typed
 * diagram toolbars) build their button arrays with these fields deliberately
 * type-erased; the Toolbar narrows them where it reads them (`createElement` for
 * the icon component, the `{ buttons, arrow }` shape for the dropdown).
 */
export interface ToolbarButton {
  /** Stable key (also the dropdown open-map key). */
  key?: string;
  /** The icon component to render on the button (a React component). */
  icon?: unknown;
  /** The text to render on the button (alternative to `icon`). */
  text?: React.ReactNode;
  /** Is the button active (e.g. its tool is currently in use)? */
  active?: boolean;
  /** Is the button disabled? */
  disabled?: boolean;
  /** The help text shown on hover. */
  title?: string;
  /** Click handler, or the literal `'dropdown'`. */
  handler?: ToolbarButtonHandler;
  /** The dropdown info ({@link ToolbarDropdownConfig}). */
  dropdown?: unknown;
}

export interface ToolbarProps {
  /** The buttons to render. */
  buttons: ToolbarButton[];
  /**
   * The variant. Used in the css class names to differentiate styling.
   */
  variant?: string;
  /** Extra class to add on the wrapper div. */
  className?: string;
}

/**
 * A toolbar with icon or text buttons, which may have dropdowns when clicked.
 *
 * Used for the toolbar in the title bar and the toolbars in the EDG and OET
 * views and the floating toolbars (undo/redo, zoom edg, ...).
 *
 * props:
 * - variant: the variant. Used in the css class names to able to differentiate
 *   styling.
 * - className: extra class to add on the wrapper div.
 * - buttons: an array of buttons. Each button has:
 *   - icon or text: the icon or text to render on the button (one of both is
 *     required)
 *   - active: is the button active (e.g. the tool corresponding to the button
 *     is currently in use)?
 *   - disabled: is the button enabled or disabled?
 *   - title: the title (help text shown on hover) for the button
 *   - handler: either the string 'dropdown' or a function to be called on
 *     button click. Set the handler to dropdown to let the toolbar handle the
 *     open/closed state of the dropdown. Otherwise the ToolbarDropdown
 *     component will handle it (needs arrow == true). Since the dropdown will
 *     then only open when the arrow button is click, you can add another
 *     handler to the button itself.
 *   - dropdown: the information for the dropdown
 *     - buttons: the buttons for the dropdown
 *     - arrow: should an arrow be rendered to open the dropdown?
 *
 * The open/closed state of the 'dropdown'-handler buttons is a dynamic map keyed
 * by `button.key`, so it stays a single `useState({})` object. The class toggled
 * one key with a functional `setState(prev => ({ [key]: !prev[key] }))` partial
 * merge — reproduced here with a functional updater that spreads `prev` so the
 * other keys are preserved (useState replaces, unlike setState which merges).
 */
export default function Toolbar({ buttons, variant, className: extraClassName }: ToolbarProps) {
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const onButtonClick = (button: ToolbarButton) => {
    if (button.handler === 'dropdown') {
      setOpenMap((prev) => ({ ...prev, [button.key!]: !prev[button.key!] }));
    } else if (button.handler && !button.disabled) {
      button.handler();
    }
  };

  // The open-props the Toolbar injects into a `'dropdown'`-handled ToolbarDropdown.
  type DropdownOpenProps = {
    useOpenProp?: boolean;
    open?: boolean;
    onRequestClose?: () => void;
  };

  const renderButtonDropdown = (button: ToolbarButton) => {
    if (button.dropdown) {
      // `dropdown` is type-erased on the button (see ToolbarButton.dropdown);
      // it always carries the `{ buttons, arrow }` shape at this point.
      const { buttons: dropdownButtons, arrow } = button.dropdown as ToolbarDropdownConfig;
      const openProps: DropdownOpenProps = {};
      if (button.handler === 'dropdown') {
        openProps.useOpenProp = true;
        openProps.open = openMap[button.key!];
        openProps.onRequestClose = () => onButtonClick(button);
      }
      return (
        <ToolbarDropdown
          {...openProps}
          buttons={dropdownButtons}
          arrow={arrow}
          variant={variant}
        />
      );
    }
  };

  const renderButtonContent = (button: ToolbarButton) => {
    if (button.icon) {
      return (
        <IconButton
          onClick={() => {
            onButtonClick(button);
          }}
          className={button.handler === 'dropdown' ? 'with-dropdown' : ''}
          active={button.active}
          disabled={button.disabled}
          variant={variant}
          title={button.title}
        >
          {/* `icon` is type-erased on the button; it is a React component. */}
          {React.createElement(button.icon as React.ComponentType)}
        </IconButton>
      );
    } else if (button.text) {
      // `text-button` stays a literal hook (diagram flows reach it for the
      // 60px floating width). `active`/`disabled` are kept as literal hooks
      // with no own rule today, matching the original behaviour.
      return (
        <div
          className={classNames('text-button select-none p-[10px] whitespace-nowrap', {
            active: button.active,
            disabled: button.disabled,
          })}
          onClick={() => onButtonClick(button)}
          title={button.title}
        >
          {button.text}
        </div>
      );
    }
  };

  const renderButton = (button: ToolbarButton) => {
    return (
      <div
        key={button.key}
        className="toolbar-button flex cursor-pointer items-stretch justify-start"
      >
        {renderButtonContent(button)}
        {renderButtonDropdown(button)}
      </div>
    );
  };

  // `toolbar` plus the variant modifier stay literal hooks (reached by the
  // diagram flows / Project / OET stylesheets). Unknown variants fall back to
  // the bare `toolbar-${variant}` hook with no extra utilities.
  const variantClass = variant
    ? VARIANT_CLASSES[variant] ?? `toolbar-${variant}`
    : undefined;
  return (
    <div className={classNames('toolbar flex items-center justify-start', variantClass, extraClassName)}>
      {buttons.map(renderButton)}
    </div>
  );
}
